import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Award, Download, Check } from "lucide-react";
import ProgressBar from "@/components/ProgressBar";
import { useAuth } from "@/contexts/AuthContext";

export default function Certifications() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const certRef = useRef(null);

    useEffect(() => {
        api.get("/certifications/me").then((r) => setData(r.data));
    }, []);

    const downloadCert = async () => {
        // Simple HTML-to-print approach: open print dialog of the certificate panel
        const node = certRef.current;
        if (!node) return;
        const w = window.open("", "_blank", "width=900,height=650");
        if (!w) return;
        w.document.write(`<!DOCTYPE html><html><head><title>HAEVN Certificate</title>
            <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,300;9..144,500&display=swap" rel="stylesheet">
            <style>body{margin:0;font-family:Manrope,sans-serif;background:#F9F9F8;color:#111;padding:40px;}</style>
            </head><body>${node.outerHTML}</body></html>`);
        w.document.close();
        setTimeout(() => w.print(), 400);
    };

    if (!data) return <div className="text-sm text-ink-500" data-testid="cert-loading">Loading…</div>;

    const { summary, certificate, completed } = data;

    return (
        <div className="space-y-10" data-testid="certifications-page">
            <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">Credential</div>
                <h1 className="font-editorial text-4xl md:text-5xl text-ink-900 leading-[1.05]">
                    HAEVN Appointment Setter Certification.
                </h1>
                <p className="mt-3 text-base text-ink-500 max-w-2xl">
                    Earned by completing every module and passing each module quiz at threshold.
                </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 bg-white border border-ink-300 p-8">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">Progress</div>
                    <div className="font-heading text-5xl text-ink-900">{summary.certification_pct}%</div>
                    <div className="mt-1 text-xs text-ink-500">toward credential</div>
                    <div className="mt-6">
                        <ProgressBar value={summary.certification_pct} size="lg" />
                    </div>
                    <div className="mt-8 space-y-3 text-sm">
                        <Row label="Modules" value={`${summary.total_modules}`} />
                        <Row label="Lessons" value={`${summary.completed_lessons}/${summary.total_lessons}`} />
                        <Row label="Quizzes passed" value={`${summary.passed_quizzes}/${summary.total_quizzes}`} />
                    </div>
                </div>

                <div className="lg:col-span-2">
                    {completed ? (
                        <div ref={certRef} className="bg-white border border-haevn-700 p-12 relative">
                            <div className="absolute top-6 right-6 w-12 h-12 rounded-full bg-haevn-700 text-white flex items-center justify-center">
                                <Award size={20} strokeWidth={1.5} />
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-ink-500 font-mono">HAEVN University</div>
                            <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono">Certificate of Completion</div>

                            <div className="mt-12">
                                <div className="text-sm text-ink-500">This certifies that</div>
                                <div className="mt-2 font-editorial text-4xl md:text-5xl text-ink-900">{user?.name}</div>
                                <div className="mt-4 text-sm text-ink-500 max-w-md">
                                    has successfully completed the HAEVN Appointment Setter Certification, demonstrating mastery of the Meetup Spots program, qualification, objection handling, and call framework.
                                </div>
                            </div>

                            <div className="mt-16 grid grid-cols-2 gap-8">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">Issued</div>
                                    <div className="text-sm mt-1">{new Date(certificate.issued_at).toLocaleDateString()}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">Credential ID</div>
                                    <div className="text-sm mt-1 font-mono">{certificate.credential_id}</div>
                                </div>
                            </div>

                            <button
                                onClick={downloadCert}
                                data-testid="cert-download-btn"
                                className="mt-10 inline-flex items-center gap-2 bg-haevn-700 text-white px-5 py-2 rounded-sm text-sm hover:bg-haevn-900 transition-colors print:hidden"
                            >
                                <Download size={14} /> Download certificate
                            </button>
                        </div>
                    ) : (
                        <div className="bg-white border border-ink-300 p-12">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono">Status</div>
                            <div className="mt-3 font-editorial text-3xl text-ink-900">In progress.</div>
                            <p className="mt-3 text-sm text-ink-500 max-w-md">
                                Continue working through the modules. Your certificate will be issued automatically the moment your last quiz passes.
                            </p>
                            <div className="mt-8 grid grid-cols-2 gap-6">
                                <Milestone label="Complete all modules" met={summary.lesson_pct >= 100} />
                                <Milestone label="Pass every quiz" met={summary.passed_quizzes === summary.total_quizzes && summary.total_quizzes > 0} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between border-b border-ink-200 pb-2">
            <span className="text-ink-500">{label}</span>
            <span className="font-mono text-ink-900">{value}</span>
        </div>
    );
}

function Milestone({ label, met }) {
    return (
        <div className={`p-4 border rounded-sm ${met ? "bg-haevn-50 border-haevn-100" : "bg-ink-100 border-ink-300"}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white ${met ? "bg-haevn-700" : "bg-ink-300"}`}>
                {met ? <Check size={12} /> : null}
            </div>
            <div className="mt-3 text-sm text-ink-900">{label}</div>
        </div>
    );
}
